import React, { useState, useEffect } from 'react';
import { LayoutGrid, Plus, X } from 'lucide-react';
import ActivityLayout, {
    Toggle, SettingsGroup, SettingsRow,
    InlineInput, AIButton, AIUndoButton, PollResults, AddImageSection, QuestionImage,
} from './ActivityLayout';
import Tooltip from '../../components/ui/Tooltip';
import styles from './ActivityLayout.module.css';
import useActivityContent from '../../hooks/useActivityContent';
import ParticipantExperience from '../join/ParticipantExperience';
import { API_PATH, request } from '../../config/api';
import { useConfirm } from '../../store/ConfirmDialogContext';

const DUMMY_DATA = {
    question: 'Sort these foods into the correct food group 🥗',
    categories: [
        { name: 'Fruits 🍎', items: ['Apple', 'Banana', 'Mango', 'Grapes'] },
        { name: 'Vegetables 🥦', items: ['Carrot', 'Spinach', 'Broccoli'] },
        { name: 'Grains 🌾', items: ['Rice', 'Wheat', 'Oats'] },
    ],
    settings: { description: true, descText: 'Drag each item to its correct food group category.', pollResults: 'hidden', isLocked: false, allowUpdateResponse: true, imageUrl: null },
};

const Categories = ({
    event, activity, isLaunched, onLaunch, onStop,
    onNext, onPrev, hasNext, hasPrev, onSettingsUpdated
}) => {
    const confirm = useConfirm();
    const [question, setQuestion] = useState('');
    const [editingQuestion, setEditingQuestion] = useState(false);
    const [editingDesc, setEditingDesc] = useState(false);
    const [categories, setCategories] = useState([
        { name: 'Category 1', items: ['Item 1'] },
        { name: 'Category 2', items: ['Item 2'] },
    ]);
    const [settings, setSettings] = useState({
        description: false,
        descText: '',
        pollResults: 'hidden',
        isLocked: false,
        allowUpdateResponse: true,
        imageUrl: null,
    });

    const { content, isLoading, isSaving, saveStatus, saveContent } =
        useActivityContent(event?.id, activity?.id);

    const [isImproving, setIsImproving] = useState(false);
    const [isImprovingDesc, setIsImprovingDesc] = useState(false);
    const [previousQuestion, setPreviousQuestion] = useState(null);
    const [previousDescText, setPreviousDescText] = useState(null);
    const API_BASE = API_PATH;

    const handleImproveWithAI = async () => {
        if (!question.trim()) return;
        setIsImproving(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text: question }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousQuestion(question);
                    setQuestion(data.suggestion);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsImproving(false);
        }
    };

    const handleUndoQuestion = () => {
        if (previousQuestion !== null) {
            setQuestion(previousQuestion);
            setPreviousQuestion(null);
        }
    };

    const handleImproveDescWithAI = async () => {
        const text = settings.descText?.trim() || '';
        if (!text) return;
        setIsImprovingDesc(true);
        try {
            const res = await request(`${API_BASE}/ai/suggest`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ type: 'improve', text }),
            });
            if (res.ok) {
                const data = await res.json();
                if (data.suggestion) {
                    setPreviousDescText(text);
                    set('descText', data.suggestion);
                }
            }
        } catch (err) {
            console.error(err);
        } finally {
            setIsImprovingDesc(false);
        }
    };

    const handleUndoDesc = () => {
        if (previousDescText !== null) {
            set('descText', previousDescText);
            setPreviousDescText(null);
        }
    };

    // Hydrate local state from DB content once loaded
    useEffect(() => {
        if (!content || Object.keys(content).length === 0) return;
        if (content.question !== undefined) setQuestion(content.question);
        if (content.categories) setCategories(content.categories);
        if (content.settings) setSettings(s => ({ ...s, ...content.settings }));
    }, [content]);

    const set = (k, v) => setSettings((s) => ({ ...s, [k]: v }));
    const openQuestionEditor = () => setEditingQuestion(true);
    const closeQuestionEditor = () => setEditingQuestion(false);
    const openDescEditor = () => setEditingDesc(true);
    const closeDescEditor = () => setEditingDesc(false);
    const handleKeyToEdit = (e, openFn) => {
        if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); openFn(); }
    };
    const handleEditKeyDown = (e, closeFn) => {
        if (e.key === 'Enter' || e.key === 'Escape') { e.preventDefault(); closeFn(); }
    };

    // ── Category helpers ────────────────────────────────────────────────────
    const updateCategoryName = (catIdx, name) => {
        setCategories(prev => prev.map((c, i) => i === catIdx ? { ...c, name } : c));
    };

    const addItemToCategory = (catIdx) => {
        setCategories(prev => prev.map((c, i) =>
            i === catIdx ? { ...c, items: [...c.items, ''] } : c
        ));
    };

    const updateItem = (catIdx, itemIdx, value) => {
        setCategories(prev => prev.map((c, i) =>
            i === catIdx
                ? { ...c, items: c.items.map((item, j) => j === itemIdx ? value : item) }
                : c
        ));
    };

    const removeItem = async (catIdx, itemIdx) => {
        const ok = await confirm({
            title: 'Delete Item',
            message: 'Delete this item?',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setCategories(prev => prev.map((c, i) =>
            i === catIdx
                ? { ...c, items: c.items.filter((_, j) => j !== itemIdx) }
                : c
        ));
    };

    const addCategory = () => {
        setCategories(prev => [...prev, { name: `Category ${prev.length + 1}`, items: [''] }]);
    };

    const removeCategory = async (catIdx) => {
        if (categories.length <= 2) return;
        const ok = await confirm({
            title: 'Delete Category',
            message: 'Delete this category?',
            confirmText: 'Delete',
            tone: 'danger',
        });
        if (!ok) return;
        setCategories(prev => prev.filter((_, i) => i !== catIdx));
    };

    // ── Right panel (settings) ──────────────────────────────────────────────
    const right = (
        <>
            <SettingsGroup title="Poll Settings">
                <SettingsRow label="Poll description" description="Add description visible to participants">
                    <Toggle checked={settings.description} onChange={(e) => set('description', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup title="Poll Results">
                <PollResults value={settings.pollResults} onChange={(v) => set('pollResults', v)} />
                <SettingsRow label="Allow participants to update response" description="Let participants change their answer after submitting">
                    <Toggle checked={settings.allowUpdateResponse !== false} onChange={(e) => set('allowUpdateResponse', e.target.checked)} />
                </SettingsRow>
            </SettingsGroup>

            <SettingsGroup>
                <AddImageSection
                    imageUrl={settings.imageUrl}
                    onImageChange={(url) => set('imageUrl', url)}
                />
            </SettingsGroup>
        </>
    );

    // ── Left panel (editor) ─────────────────────────────────────────────────
    const left = (
        <>
            <div className={`${styles.questionCard} ${settings.imageUrl ? styles.questionCardWithImage : ''}`}>
                {settings.imageUrl && (
                    <QuestionImage imageUrl={settings.imageUrl} onImageChange={(url) => set('imageUrl', url)} />
                )}
                <div className={styles.questionContent}>
                    <div className={styles.questionLabelRow}>
                        <span className={styles.questionLabel}>Question <span className={styles.requiredStar}>*</span></span>
                        <span className={styles.aiActions}>
                            <AIButton onClick={handleImproveWithAI} loading={isImproving} />
                            {previousQuestion !== null && (
                                <AIUndoButton onClick={handleUndoQuestion} label="Undo" />
                            )}
                        </span>
                    </div>
                    {editingQuestion ? (
                        <input
                            className={styles.questionInput}
                            value={question}
                            onChange={(e) => setQuestion(e.target.value)}
                            placeholder="Assign each item to its correct category..."
                            autoFocus
                            onBlur={closeQuestionEditor}
                            onKeyDown={(e) => handleEditKeyDown(e, closeQuestionEditor)}
                        />
                    ) : (
                        <Tooltip title="Click to edit question" placement="top">
                            <div
                                className={`${styles.questionTextDisplay} ${!question ? styles.questionTextPlaceholder : ''}`}
                                role="button"
                                tabIndex={0}
                                onClick={openQuestionEditor}
                                onKeyDown={(e) => handleKeyToEdit(e, openQuestionEditor)}
                            >
                                {question || 'Type your question here...'}
                            </div>
                        </Tooltip>
                    )}

                    {settings.description && (
                        <div className={styles.questionDescription}>
                            <div className={styles.questionLabelRow}>
                                <span className={styles.descLabel}>Description</span>
                                <span className={styles.aiActions}>
                                    <AIButton onClick={handleImproveDescWithAI} loading={isImprovingDesc} />
                                    {previousDescText !== null && (
                                        <AIUndoButton onClick={handleUndoDesc} label="Undo" />
                                    )}
                                </span>
                            </div>
                            {editingDesc ? (
                                <input
                                    className={styles.descInput}
                                    value={settings.descText}
                                    onChange={(e) => set('descText', e.target.value)}
                                    placeholder="Type description here..."
                                    autoFocus
                                    onBlur={closeDescEditor}
                                    onKeyDown={(e) => handleEditKeyDown(e, closeDescEditor)}
                                />
                            ) : (
                                <Tooltip title="Click to edit description" placement="top">
                                    <div
                                        className={`${styles.descTextDisplay} ${!settings.descText ? styles.descTextPlaceholder : ''}`}
                                        role="button"
                                        tabIndex={0}
                                        onClick={openDescEditor}
                                        onKeyDown={(e) => handleKeyToEdit(e, openDescEditor)}
                                    >
                                        {settings.descText || 'Type description here...'}
                                    </div>
                                </Tooltip>
                            )}
                        </div>
                    )}
                </div>
            </div>

            {/* How it works */}
            <div className={`${styles.infoCard} ${styles.infoCardPurple}`}>
                <div className={styles.infoCardLabel}>How Categories works</div>
                <p className={styles.infoCardText}>
                    <strong>You:</strong> Add categories and put the correct items in each (the items listed under a category are the correct answers). Participants will see all items in a shuffled pool and must assign each item to the right category. Results show how many assignments were correct.
                </p>
            </div>

            {/* Categories */}
            {categories.map((cat, catIdx) => (
                <div key={catIdx} className={styles.categoryCard}>
                    {/* Category header */}
                    <div className={styles.categoryHeader}>
                        <input
                            className={styles.categoryNameInput}
                            value={cat.name}
                            onChange={(e) => updateCategoryName(catIdx, e.target.value)}
                            placeholder={`Category ${catIdx + 1}`}
                        />
                        {categories.length > 2 && (
                            <Tooltip title="Remove category" placement="top">
                                <button
                                    type="button"
                                    className={styles.categoryDeleteBtn}
                                    onClick={() => removeCategory(catIdx)}
                                    aria-label="Remove category"
                                >
                                    <X size={14} />
                                </button>
                            </Tooltip>
                        )}
                    </div>

                    {/* Items within this category */}
                    {cat.items.map((item, itemIdx) => (
                        <div key={itemIdx} className={styles.optionRow}>
                            <div className={`${styles.optionDot} ${styles.categoryItemDot}`} />
                            <input
                                className={styles.optionInput}
                                value={item}
                                onChange={(e) => updateItem(catIdx, itemIdx, e.target.value)}
                                placeholder={`Item ${itemIdx + 1}`}
                            />
                            {cat.items.length > 1 && (
                                <button
                                    type="button"
                                    className={styles.optionRemoveBtn}
                                    onClick={() => removeItem(catIdx, itemIdx)}
                                    aria-label="Remove item"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                    ))}

                    {/* Add item button */}
                    <div className={styles.optionsFooter}>
                        <button type="button" className={styles.addOptionBtn} onClick={() => addItemToCategory(catIdx)}>
                            <Plus size={14} /> Add item
                        </button>
                    </div>
                </div>
            ))}

            {/* Add category button */}
            <button type="button" className={styles.addCategoryBtn} onClick={addCategory}>
                <Plus size={16} /> Add category
            </button>
        </>
    );

    const loadDummy = () => {
        setQuestion(DUMMY_DATA.question);
        setCategories(DUMMY_DATA.categories);
        setSettings(s => ({ ...s, ...DUMMY_DATA.settings }));
    };

    const participantPreview = (
        <ParticipantExperience
            step="activity"
            name="Preview User"
            event={event}
            activity={{
                type: 'categories',
                title: question || 'Categories',
                content: { question, categories, settings }
            }}
        />
    );

    return (
        <ActivityLayout
            icon={<LayoutGrid size={20} />}
            color="var(--cat-violet)"
            title="Categories"
            eventName={event?.name}
            event={event}
            activity={activity}
            left={left}
            right={right}
            participantPreview={participantPreview}
            validate={() => !question.trim() ? 'Question is required.' : null}
            onSave={() => {
                setPreviousQuestion(null);
                setPreviousDescText(null);
                saveContent({ question, categories, settings });
            }}
            onLoadExample={loadDummy}
            isSaving={isSaving}
            saveStatus={saveStatus}
            isLoading={isLoading}
            isLaunched={isLaunched}
            onLaunch={onLaunch}
            onStop={onStop}
            onNext={onNext}
            onPrev={onPrev}
            hasNext={hasNext}
            hasPrev={hasPrev}
            onSettingsUpdated={onSettingsUpdated}
        />
    );
};

export default Categories;
